/**
 * Canonical SEO URL pattern for activity pages:
 * `/<lang>/<city>/<category-or-segment>/<slug>`
 *
 * - Bookable items use `categorySlug` (e.g. "quad", "excursions").
 * - Explore items use `segment` (e.g. "restaurants", "locations") since
 *   they have no category.
 * - Falls back to "place" when neither is available.
 */
export interface ActivityHrefInput {
  lang: string;
  citySlug?: string | null;
  categorySlug?: string | null;
  segment?: string | null;
  slug: string;
}

export function activityHrefParts(input: ActivityHrefInput): {
  to: "/$lang/$city/$category/$slug";
  params: { lang: string; city: string; category: string; slug: string };
} {
  const city = input.citySlug?.trim() || "morocco";
  const category =
    input.categorySlug?.trim() || input.segment?.trim() || "place";
  return {
    to: "/$lang/$city/$category/$slug",
    params: { lang: input.lang, city, category, slug: input.slug },
  };
}

/**
 * The category of the transfer routes. A route has no page of its own (owner
 * decision): it is booked on the transport hub with the route preselected.
 * Mirrors apps/majourneys/src/features/activities/lib/activity-href.ts.
 */
export const TRANSPORT_CATEGORY_SLUG = "transport";

/** Whether an activity is a transfer route, by category or by template. */
export function isTransferRoute(a: { categorySlug?: string | null; template?: string | null }): boolean {
  return (a.categorySlug ?? "").trim().toLowerCase() === TRANSPORT_CATEGORY_SLUG || a.template === "transfer";
}

/** The hub with one route preselected: `/<lang>/transport?route=<slug>`. */
export function transportHubHref(lang: string, slug: string): string {
  return `/${lang}/transport?route=${encodeURIComponent(slug)}`;
}

export function activityHrefString(input: ActivityHrefInput): string {
  const p = activityHrefParts(input);
  return `/${p.params.lang}/${p.params.city}/${p.params.category}/${p.params.slug}`;
}
